import React from "react";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import { AlertWarningIcon } from "@adsk/alloy-react-icon";
import Theme from "@adsk/alloy-react-theme";
import { FIELD_WRAPPER_FONT } from "@adsk/acc-issues-core/utils/styles/consts";
import { useTranslations } from "@adsk/acc-issues-translations";
import { Pagination } from "@adsk/acc-issues-types";

interface IssueCommentsPanelLoaderProps {
  isLoading: boolean;
  isErrored: boolean;
  shownCommentsCount: number;
  totalCommentsCount: number;
  canRequestMoreComments: boolean;
  onRequestMoreComments: (offset: number) => void;
  onRetry: (offset: number) => void;
  paginationState: Pagination | undefined;
}

const Link = ({ onClick, text }: { onClick: () => void; text: string }) => (
  <a
    onClick={onClick}
    style={{
      cursor: "pointer",
    }}
  >
    {text}
  </a>
);

export const IssueCommentsPanelHeader = ({
  isLoading,
  isErrored,
  shownCommentsCount,
  totalCommentsCount,
  canRequestMoreComments,
  onRequestMoreComments,
  onRetry,
  paginationState,
}: IssueCommentsPanelLoaderProps) => {
  const { T } = useTranslations();
  const isEmpty = totalCommentsCount === 0;
  const showCount = shownCommentsCount > 0 && totalCommentsCount > 0;
  const nextOffset = paginationState
    ? paginationState.offset + paginationState.limit
    : 0;

  return (
    <>
      <div
        data-testid="comments-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "11px",
          ...FIELD_WRAPPER_FONT,
        }}
      >
        <span>{T.translate("issueComments.title")}</span>
        {showCount && (
          <span>
            {T.translate("issueComments.shownCount", {
              shownComments: shownCommentsCount.toLocaleString(),
              totalComments: totalCommentsCount.toLocaleString(),
            })}
          </span>
        )}
      </div>
      {(isLoading || isErrored || canRequestMoreComments || isEmpty) && (
        <div
          style={{
            color: Theme.colors.charcoal500,
            height: "45px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <ProgressRing size={ProgressRing.SIZES.SMALL} />
          ) : (
            <>
              {isErrored ? (
                <span
                  style={{
                    display: "flex",
                  }}
                >
                  <AlertWarningIcon
                    style={{
                      marginRight: `${8}px`,
                    }}
                  />
                  <span
                    style={{
                      marginRight: `${4}px`,
                    }}
                  >
                    {T.translate("issueComments.loadFailureMessage")}
                  </span>
                  <Link
                    onClick={() => onRetry(nextOffset)}
                    text={T.translate("tryAgain") as string}
                  />
                </span>
              ) : canRequestMoreComments ? (
                <Link
                  onClick={() => onRequestMoreComments(nextOffset)}
                  text={T.translate("issueComments.loadMore") as string}
                />
              ) : (
                isEmpty && <span>{T.translate("issueComments.empty")}</span>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};
